/**
 * SupportFormModal — V2.8.8 — Landing-page account-recovery form.
 * Public: anyone can submit. Backend stores ticket; admins review.
 * Routes to: institutionalsandboxsupport@gmail.com
 */
import { useState } from "react";
import api, { fmtErr } from "@/lib/api";

const SUPPORT_EMAIL = "institutionalsandboxsupport@gmail.com";

export default function SupportFormModal({ onClose }) {
  const [form, setForm] = useState({ email: "", password: "", subject: "", body: "", kind: "recovery" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/support/ticket", form);
      setDone(true);
    } catch (ex) { setErr(fmtErr(ex.response?.data?.detail) || ex.message); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="support-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 font-mono">
      <div className="bg-[#0f131b] border border-blue-500/30 rounded-2xl max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-600/20 text-blue-300 rounded-lg border border-blue-500/20"><i className="fa-solid fa-life-ring"/></div>
            <div>
              <div className="text-base font-bold text-white">Contact Support</div>
              <div className="text-[10px] text-slate-500">{SUPPORT_EMAIL}</div>
            </div>
          </div>
          <button data-testid="support-close" onClick={onClose} className="text-slate-500 hover:text-rose-300 text-xl"><i className="fa-solid fa-xmark"/></button>
        </div>

        {done ? (
          <div className="text-center py-6 space-y-3" data-testid="support-success">
            <i className="fa-solid fa-circle-check text-4xl text-emerald-400"/>
            <div className="text-white font-bold text-sm">Ticket submitted</div>
            <p className="text-xs text-slate-400">Our admins will review your request. You'll typically hear back within 24 hours at the email you provided.</p>
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold text-white">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-slate-400">
              Locked out? Account taken over? Fill in the form below — your password is captured so an admin can verify your ownership and reset access. Use the email associated with your account.
            </p>
            <Field label="Topic">
              <select data-testid="support-kind" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm">
                <option value="recovery">Account Recovery / Login Issue</option>
                <option value="billing">Billing / Refund</option>
                <option value="bug">Bug Report</option>
                <option value="other">Something else</option>
              </select>
            </Field>
            <Field label="Your account email">
              <input data-testid="support-email" type="email" required value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@gmail.com"
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
            </Field>
            {form.kind === "recovery" && (
              <Field label="Last known password (for ownership verification)">
                <input data-testid="support-password" type="password" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="(optional but speeds up recovery)"
                  className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
                <div className="text-[10px] text-slate-600 mt-1">We'll rotate this immediately after verifying — never share it elsewhere.</div>
              </Field>
            )}
            <Field label="Subject">
              <input data-testid="support-subject" value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                placeholder="Brief summary"
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
            </Field>
            <Field label="Describe your issue">
              <textarea data-testid="support-body" required minLength={10} maxLength={5000}
                value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                rows={5}
                placeholder="Tell us what happened, when it started, and what you've already tried."
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm resize-none"/>
            </Field>
            {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5" data-testid="support-error">{err}</div>}
            <button data-testid="support-submit" type="submit" disabled={busy}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold">
              {busy ? "Submitting…" : <><i className="fa-solid fa-paper-plane mr-1.5"/>Submit Ticket</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1">{label}</div>{children}</div>;
}
